import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext } from 'react-beautiful-dnd';
import {
  FDAccordion, FDAccordionDetails, FDAccordionTitle, FDPaper, FDStack,
} from '@facet-wealth/design';
import { LabelTextMedium } from 'Packages/FacetDesign/Components';
import Divider from 'Components/UI/Divider';
import { DroppableColumn } from './droppableColumn';
import useSmartAllocationDragAndDrop from './useSmartAllocationDragAndDrop';

const SmartAutoCustomizationGroupList = ({
  groupResource,
  optimisticUpdateSacGroup,
  isOptimisticallyUpdating,
  sacGroupsCollection,
  showActiveStatus,
  StepComponent,
  onlyDragContent,
}) => {
  const {
    onDragEnd,
    activeGroupSteps,
    inactiveGroupSteps,
    onBreakGroup,
    isNeverDraggable,
    isCurrentlyNotDraggable,
    isCompatibleDropTarget,
  } = useSmartAllocationDragAndDrop({
    groupResource,
    optimisticUpdateSacGroup,
    isOptimisticallyUpdating,
    sacGroupsCollection,
  });

  const [expanded, setExpanded] = useState(false);

  const canBeRanked = groupResource.isTruthy('canBeRanked');

  const canBeDeactivated = groupResource.type() !== 'facetPlanSmartAllocationCustomizationOutcomeGroup' && groupResource.type() !== 'GuidedHouseholdReviewGoalPriorityStepSet';

  const dragContent = (
    <DragDropContext onDragEnd={onDragEnd}>
      <FDStack direction={'column'} padding={onlyDragContent ? 0 : 1} spacing={1}>
        <DroppableColumn
          StepComponent={StepComponent}
          groupTitle={groupResource.attr('title')}
          isActiveStatus={'Active'}
          showActiveStatus={showActiveStatus}
          groupSteps={activeGroupSteps}
          onBreakGroup={onBreakGroup}
          isNeverDraggable={isNeverDraggable}
          isCurrentlyNotDraggable={isCurrentlyNotDraggable}
          isCompatibleDropTarget={isCompatibleDropTarget}
        />
        {canBeRanked && canBeDeactivated && <>
          <Divider />
          <DroppableColumn
            StepComponent={StepComponent}
            groupTitle={groupResource.attr('title')}
            isActiveStatus={'Inactive'}
            showActiveStatus={showActiveStatus}
            groupSteps={inactiveGroupSteps}
            onBreakGroup={onBreakGroup}
            isNeverDraggable={isNeverDraggable}
            isCurrentlyNotDraggable={isCurrentlyNotDraggable}
            isCompatibleDropTarget={isCompatibleDropTarget}
          />
        </>}
      </FDStack>
    </DragDropContext>
  );
  if (onlyDragContent) {
    return dragContent;
  }

  return (
    <FDPaper color={'subtle'} padding={1}>
      {!canBeRanked && <>
        <FDAccordion expanded={expanded} onChange={setExpanded}>
          <FDAccordionTitle>
            <LabelTextMedium
              color={'textPrimary'}
              value={`${groupResource.selectedOptionPrompt('title')} Steps`}
            />
          </FDAccordionTitle>
          <FDAccordionDetails>
            {dragContent}
          </FDAccordionDetails>
        </FDAccordion>
      </>}
      {canBeRanked && dragContent}
    </FDPaper>
  );
};

SmartAutoCustomizationGroupList.propTypes = {
  groupResource: PropTypes.object.isRequired,
  optimisticUpdateSacGroup: PropTypes.func.isRequired,
  isOptimisticallyUpdating: PropTypes.bool.isRequired,
  sacGroupsCollection: PropTypes.object,
  StepComponent: PropTypes.func.isRequired,
  showActiveStatus: PropTypes.bool,
  onlyDragContent: PropTypes.bool,
};

export default SmartAutoCustomizationGroupList;
